/**
 * One refused photo, decided IN PLACE on its review page (spec 2026-10-13 §6; since
 * 2026-10-19 the review page is the one place to decide, and refused photos are kept with
 * no time limit). A pending photo offers Restore, Confirm refusal and Legal hold; a
 * confirmed photo whose file is kept can still be revealed and put on legal hold; a
 * restored photo, or one deleted under the former rule, shows its decision only.
 *
 * Pure: everything arrives in props, and every action is a callback, so
 * render-tests/refused-photos.render.test.tsx draws it to HTML. The rules it shows
 * are the server's: a blurred photo carries no URL until an audited reveal, Restore
 * needs room in the section and (for a blurred photo) this reviewer's reveal, and a
 * control the admin has no permission for is not rendered. No browser dialogs: the
 * reveal reason and the legal-hold confirmation are inline.
 */
import { useState } from 'react';
import { AlertTriangle, CheckCircle, EyeOff, ImageIcon, Loader2, Lock, RotateCcw, Shield, XCircle } from 'lucide-react';
import {
  MIN_REVEAL_REASON,
  REASON_LABELS,
  restoreBlockReason,
  timeLeftLabel,
  type QuarantineItem,
} from './refused-photos-model';

export interface CardPermissions {
  reveal: boolean;
  approve: boolean;
  reject: boolean;
  legalHold: boolean;
}

export interface RefusedPhotoCardProps {
  item: QuarantineItem;
  now?: Date;
  permissions: CardPermissions;
  /** The one-look URL of an audited reveal made in this session, or null. */
  revealedUrl: string | null;
  busy: boolean;
  error: string | null;
  onReveal: (reason: string) => void;
  onConfirm: (notes: string) => void;
  onRestore: (notes: string) => void;
  onEscalate: (notes: string) => void;
  onOpenUser: (userId: string) => void;
}

const SECTION_LABEL = { public: 'Public', private: 'Private' } as const;

const DECIDED_LABEL: Record<string, string> = {
  confirmed: 'Refusal confirmed',
  restored: "Restored — the photo is back in the member's photos",
  legal_hold: 'On legal hold — kept for a child-safety report',
  expired: 'Deleted under the former 15-day rule',
};

function decidedOn(item: QuarantineItem): string {
  if (!item.decided_at) return '';
  const d = new Date(item.decided_at);
  return Number.isNaN(d.getTime()) ? '' : ` on ${d.toUTCString().replace(/:\d\d GMT$/, ' UTC')}`;
}

const PHOTO_SUBJECT_CATEGORY_LABELS: Record<string, string> = {
  text_or_screenshot: 'Text / screenshot',
  not_a_person: 'Not a person photo',
  person_not_main_subject: 'Person not the main subject',
  minor_primary: 'Child as primary subject',
  moderation_unparseable: 'Unparseable model output',
};

function FailedCheckDetails({ item }: { item: QuarantineItem }) {
  const failed = item.checks.filter((c) => c.result === 'fail');
  if (failed.length === 0) return null;
  return (
    <div className="space-y-1 text-xs">
      {failed.map((c, i) => {
        const d = (c.details && typeof c.details === 'object' ? c.details : {}) as Record<string, unknown>;
        const items = Array.isArray(d.detected_items) ? (d.detected_items as unknown[]).map(String) : [];
        const labels = Array.isArray(d.labels)
          ? (d.labels as Array<{ Name?: string; Confidence?: number }>).filter((l) => Number(l.Confidence ?? 0) > 50)
          : [];
        return (
          <div key={`${c.check_type}-${i}`} className="rounded-md bg-red-50 px-2 py-1 text-red-800">
            {c.check_type === 'photo_subject' && typeof d.category === 'string' && (
              <p className="font-medium">{PHOTO_SUBJECT_CATEGORY_LABELS[d.category] ?? d.category}</p>
            )}
            {typeof d.reason === 'string' && d.reason.trim() !== '' && <p>{d.reason}</p>}
            {items.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {items.map((v, j) => (
                  <span key={j} className="rounded-full bg-red-100 px-2 py-0.5">{v}</span>
                ))}
              </div>
            )}
            {labels.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {labels.map((l, j) => (
                  <span key={j} className="rounded-full bg-red-100 px-2 py-0.5">
                    {l.Name} ({Number(l.Confidence ?? 0).toFixed(0)}%)
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** At most one decision is armed: its warning panel is open and it waits for a second click. */
export type ArmedDecision = 'none' | 'confirm' | 'hold';

export interface DecisionControlsProps {
  permissions: CardPermissions;
  busy: boolean;
  /** Why Restore is disabled, or null. */
  blockReason: string | null;
  notes: string;
  armed: ArmedDecision;
  onArm: (armed: ArmedDecision) => void;
  onRestore: (notes: string) => void;
  onConfirm: (notes: string) => void;
  onEscalate: (notes: string) => void;
}

/** Restore, Confirm refusal and Legal hold, and the warning panel of the armed one. */
export function DecisionControls({
  permissions, busy, blockReason, notes, armed, onArm, onRestore, onConfirm, onEscalate,
}: DecisionControlsProps) {
  // Every permitted button stays in place while one is armed: removing the armed button moved
  // the next one under the cursor (PREDEV click test 2026-10-14), so a double click on Confirm
  // refusal landed on Legal hold.
  const armedRing = 'ring-2 ring-offset-1 ring-red-500';
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {permissions.approve && (
          <button
            type="button"
            onClick={() => onRestore(notes)}
            disabled={busy || blockReason !== null}
            title={blockReason ?? undefined}
            className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />} Restore
          </button>
        )}
        {permissions.reject && (
          <button
            type="button"
            onClick={() => onArm('confirm')}
            disabled={busy}
            aria-pressed={armed === 'confirm'}
            className={`flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 ${armed === 'confirm' ? armedRing : ''}`}
          >
            <XCircle className="h-3 w-3" /> Confirm refusal
          </button>
        )}
        {permissions.legalHold && (
          <button
            type="button"
            onClick={() => onArm('hold')}
            disabled={busy}
            aria-pressed={armed === 'hold'}
            className={`flex items-center gap-1 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 ${armed === 'hold' ? armedRing : ''}`}
          >
            <Lock className="h-3 w-3" /> Legal hold
          </button>
        )}
      </div>
      {permissions.approve && blockReason && <p className="text-xs text-muted-foreground">{blockReason}</p>}
      {permissions.reject && armed === 'confirm' && (
        <div className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900">
          <p className="font-medium">
            Confirm the refusal? The photo stays out of the member's photos and is kept, blurred if explicit, until
            the member deletes the account.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { onArm('none'); onConfirm(notes); }}
              disabled={busy}
              className="rounded bg-red-600 px-3 py-1 font-medium text-white disabled:opacity-50"
            >
              Confirm now
            </button>
            <button type="button" onClick={() => onArm('none')} className="rounded px-3 py-1">
              Cancel
            </button>
          </div>
        </div>
      )}
      {permissions.legalHold && armed === 'hold' && (
        <div className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-900">
          <p className="flex items-start gap-1 font-medium">
            <Shield className="mt-0.5 h-3 w-3 flex-none" />
            Legal hold is for possible child sexual abuse material only. The file is kept outside the member's
            account, the owner is alerted, and the photo must be reported (NCMEC CyberTipline).
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { onArm('none'); onEscalate(notes); }}
              disabled={busy}
              className="rounded bg-red-700 px-3 py-1 font-medium text-white disabled:opacity-50"
            >
              Put on legal hold
            </button>
            <button type="button" onClick={() => onArm('none')} className="rounded px-3 py-1">
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function RefusedPhotoCard({
  item, now, permissions, revealedUrl, busy, error, onReveal, onConfirm, onRestore, onEscalate, onOpenUser,
}: RefusedPhotoCardProps) {
  const [revealOpen, setRevealOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [armed, setArmed] = useState<ArmedDecision>('none');
  const [previewFailed, setPreviewFailed] = useState(false);

  const pending = item.decision === 'pending';
  const fileKept = item.file_kept !== false;
  // What this reviewer may still do: everything while it waits; only a legal hold once a
  // confirmed refusal keeps its file (a later child-safety finding); nothing otherwise.
  const allowed: CardPermissions = pending
    ? permissions
    : { ...permissions, approve: false, reject: false, legalHold: permissions.legalHold && item.decision === 'confirmed' && fileKept };
  const canDecide = allowed.approve || allowed.reject || allowed.legalHold;
  const blockReason = restoreBlockReason(item, revealedUrl !== null);
  const reasonOk = reason.trim().length >= MIN_REVEAL_REASON;
  // What happened to THIS photo's file — never one text for every decided photo (a legal
  // hold once read "deleted under the former 15-day rule" above "On legal hold").
  const fileGoneText = item.decision === 'restored'
    ? "The photo is back in the member's photos (see All Photos by User)."
    : item.decision === 'legal_hold'
      ? "The file is kept outside the member's files for a child-safety report. It is not shown here."
      : item.decision === 'confirmed'
        ? 'No file is kept: the refusal was confirmed before 2026-10-19, when Confirm still deleted the file.'
        : 'No file is kept: it was deleted under the former 15-day rule.';

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card" data-photo-id={item.photo_id}>
      {/* The photo: a reveal, the server blur, or the preview */}
      <div className="relative bg-muted">
        {!fileKept ? (
          <div className="flex h-64 flex-col items-center justify-center gap-2 p-4 text-center text-sm text-muted-foreground">
            <ImageIcon className="h-10 w-10" /> {fileGoneText}
          </div>
        ) : revealedUrl ? (
          <>
            <img src={revealedUrl} alt="Refused photo (revealed)" className="h-64 w-full object-contain" />
            <div className="absolute left-2 top-2 rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-medium text-white">
              Revealed — your reveal is logged. The link works for 60 seconds.
            </div>
          </>
        ) : item.blurred ? (
          <>
            {item.blurred_preview ? (
              <img src={item.blurred_preview} alt="Blurred refused photo" className="h-64 w-full scale-110 object-cover blur-md" />
            ) : (
              <div className="flex h-64 items-center justify-center">
                <EyeOff className="h-10 w-10 text-muted-foreground" />
              </div>
            )}
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/30 p-4 text-center text-white">
              <span className="flex items-center gap-1 text-sm font-medium"><EyeOff className="h-4 w-4" /> Explicit — blurred</span>
              {permissions.reveal ? (
                !revealOpen ? (
                  <button
                    type="button"
                    onClick={() => setRevealOpen(true)}
                    disabled={busy}
                    className="rounded-lg bg-white/90 px-3 py-1.5 text-sm font-medium text-gray-900 hover:bg-white disabled:opacity-50"
                  >
                    Reveal
                  </button>
                ) : (
                  <div className="w-full max-w-xs space-y-2 rounded-lg bg-white p-3 text-left text-gray-900">
                    <label className="text-xs font-medium" htmlFor={`reveal-${item.photo_id}`}>
                      Why do you need to see this photo? (kept in the audit log)
                    </label>
                    <textarea
                      id={`reveal-${item.photo_id}`}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      rows={2}
                      className="w-full rounded border p-2 text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => onReveal(reason)}
                        disabled={busy || !reasonOk}
                        className="rounded bg-amber-600 px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
                      >
                        Reveal (logged)
                      </button>
                      <button type="button" onClick={() => setRevealOpen(false)} className="rounded px-3 py-1 text-xs">
                        Cancel
                      </button>
                    </div>
                  </div>
                )
              ) : (
                <span className="text-xs">You need the moderation.photos.reveal permission to see it.</span>
              )}
            </div>
          </>
        ) : item.preview_url && !previewFailed ? (
          <img
            src={item.preview_url}
            alt="Refused photo"
            className="h-64 w-full object-contain"
            onError={() => setPreviewFailed(true)}
          />
        ) : (
          <div className="flex h-64 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
            <ImageIcon className="h-10 w-10" /> The file could not be loaded.
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <button type="button" onClick={() => onOpenUser(item.user_id)} className="group text-left">
            <div className="font-medium text-primary group-hover:underline">{item.display_name || 'No name'}</div>
            <div className="text-xs text-muted-foreground">{item.email}</div>
          </button>
          <span className="whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {pending ? timeLeftLabel(item.expires_at, now) : 'Decided'}
          </span>
        </div>

        <div className="flex flex-wrap gap-1">
          {item.reasons.map((r) => (
            <span
              key={r}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                r === 'explicit_content' ? 'bg-red-100 text-red-800' : r === 'photo_subject' ? 'bg-violet-100 text-violet-800' : 'bg-amber-100 text-amber-800'
              }`}
            >
              <AlertTriangle className="h-3 w-3" /> {REASON_LABELS[r] ?? r}
            </span>
          ))}
        </div>

        <FailedCheckDetails item={item} />

        {!pending && (
          <div className="rounded-md border bg-muted/40 px-2 py-1 text-xs font-medium" data-decision={item.decision}>
            {(DECIDED_LABEL[item.decision] ?? `Decided (${item.decision})`) + decidedOn(item)}
          </div>
        )}

        {pending && (
        <div className="text-xs text-muted-foreground">
          Restore to: {SECTION_LABEL[item.target_section]}{' '}
          {item.replacing_photo_id
            ? '(the section of the photo it was meant to replace — that photo stays, this one is added)'
            : item.requested_visibility ? "(the member's choice)" : '(by the upload rule)'}
          {item.section_full && (
            <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-800">Section full</span>
          )}
        </div>
        )}

        {error && (
          <div role="alert" className="rounded-md border border-destructive bg-destructive/10 p-2 text-xs text-destructive">
            {error}
          </div>
        )}

        {canDecide ? (
          <div className="mt-auto space-y-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Note (optional, kept in the audit log)"
              aria-label="Decision note"
              className="w-full rounded-lg border bg-background p-2 text-xs"
            />
            <DecisionControls
              permissions={allowed} busy={busy} blockReason={blockReason} notes={notes} armed={armed}
              onArm={setArmed} onRestore={onRestore} onConfirm={onConfirm} onEscalate={onEscalate}
            />
          </div>
        ) : (
          <p className="mt-auto flex items-center gap-1 text-xs text-muted-foreground">
            <CheckCircle className="h-3 w-3" />{' '}
            {pending ? 'You can view refused photos but not decide them.' : 'Decided. Nothing more to decide here.'}
          </p>
        )}
      </div>
    </div>
  );
}
